import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ColumnDefinition } from 'src/app/shared/model/column-definition';
import { SortBy } from 'src/app/shared/model/data-source';
import { Operator } from 'src/app/shared/model/operator';
import { RequestCriteria } from 'src/app/shared/model/request-criteria';
import { Transformations } from 'src/app/shared/model/transformations';
import { DataLoader } from 'src/app/shared/util/data-loader';
import { extractJsonArrayAtPath } from 'src/app/shared/util/json-array-stream-parser';
import { DataPrintService } from './data-print.service';
import { ToastService } from './toast.service';

export interface AllPagesExportState {
  exportType: 'pdf' | 'xlsx' | 'csv';
  phase: 'fetching' | 'converting';
  progress: number;
  fetchedBytes: number;
}

export interface AllPagesExportConfig<T = any> {
  dataLoader: DataLoader<T>;
  columns: ColumnDefinition[];
  transformations: Transformations;
  exportType: 'pdf' | 'xlsx' | 'csv';
  pageSize: number;
  exportPageSize?: number | null;
  /** null/undefined = service default (DEFAULT_MAX_EXPORT_ROWS); pass Infinity for "no cap". */
  maxExportRows?: number | null;
  /** Cały zbiór jest już w pamięci (np. tryb dzienny) - eksport bierze dane z getAllLoadedData() bez pobierania stron. */
  allDataPreloaded?: boolean;
}

/** config plus the row count resolved via a fetchPage probe right before fetching - never trust a caller-supplied total. */
type ResolvedExportConfig<T> = AllPagesExportConfig<T> & { total: number };

/** Applies when a DataSource doesn't declare its own maxExportRows. ~Excel's own per-sheet row ceiling (1_048_576). */
const DEFAULT_MAX_EXPORT_ROWS = 1_000_000;

/** Row window for the fetchPage probe that reads totalRows - see resolveTotal. */
const COUNT_PROBE_ROWS = 1;

/** Last-resort row window when the total is unknown AND the caller disabled the row cap (maxExportRows: Infinity) - see fetchAllWindow. */
const FETCH_ALL_ROWS = 9_999_999;

/**
 * Page size for fetchPage exports when the loader has no streamed endpoint and the DataSource
 * doesn't set its own exportPageSize. A single huge JSON response is not an option there: past
 * ~0.5 GB the browser can't materialize the response text (V8 string limit) and HttpClient
 * yields a null body.
 */
const DEFAULT_EXPORT_PAGE_SIZE = 100_000;

@Injectable({ providedIn: 'root' })
export class AllPagesExportService {
  private stateSubject = new BehaviorSubject<AllPagesExportState | null>(null);
  state$: Observable<AllPagesExportState | null> = this.stateSubject.asObservable();
  isBusy$: Observable<boolean> = this.stateSubject.pipe(map(s => s != null));

  private activeDataLoader: DataLoader | null = null;
  private cancelSignal: Subject<void> | null = null;
  private aborted = false;
  private exportStartMs = 0;

  constructor(
    private zone: NgZone,
    private printService: DataPrintService,
    private toast: ToastService
  ) {}

  get isBusy(): boolean { return this.stateSubject.value != null; }

  cancel(): void {
    if (!this.isBusy) return;
    this.aborted = true;
    this.cancelSignal?.next();
    this.cancelSignal = null;
    if (typeof (this.activeDataLoader as any)?.cancel === 'function') {
      (this.activeDataLoader as any).cancel();
    }
    if (this.stateSubject.value?.phase === 'converting') {
      this.printService.cancelAllExport();
    }
  }

  start<T>(config: AllPagesExportConfig<T>): void {
    if (this.isBusy) return;
    const canUsePreloaded = config.allDataPreloaded && !!config.dataLoader?.getAllLoadedData;
    if (!canUsePreloaded && !config.dataLoader?.fetchAll && !config.dataLoader?.fetchPage) return;

    this.aborted = false;
    this.activeDataLoader = config.dataLoader;
    this.cancelSignal = new Subject<void>();
    this.exportStartMs = performance.now();
    this.setState({ exportType: config.exportType, phase: 'fetching', progress: 0, fetchedBytes: 0 });

    this.zone.runOutsideAngular(() => {
      this.runExport(config).catch(err => this.showExportError(err));
    });
  }

  /**
   * Server-side timeout/overload for an "all pages" export almost always means the
   * criteria are too broad. Surface an actionable hint instead of a raw error string.
   */
  private showExportError(err: unknown): void {
    const isTimeout = err instanceof HttpErrorResponse
      ? [0, 408, 500, 502, 503, 504, 524].includes(err.status)
      : /timeout|timed out|truncated|unexpected end of buffer/i.test(String(err));

    if (isTimeout) {
      this.zone.run(() => this.toast.showErrorToast('local-print.timeout-warning'));
      return;
    }

    const detail = err instanceof HttpErrorResponse
      ? (err.error?.message ?? err.message ?? err.statusText)
      : err instanceof Error ? err.message : String(err);
    this.zone.run(() => this.toast.showErrorToast('local-print.export-error', 'error', { detail }));
  }

  private setState(state: AllPagesExportState | null): void {
    if (NgZone.isInAngularZone()) {
      this.stateSubject.next(state);
    } else {
      this.zone.run(() => this.stateSubject.next(state));
    }
  }

  /** Nazwa pliku zapisanego przez bieżący eksport - trafia do toastu sukcesu. */
  private lastSavedFileName: string | null = null;

  private async runExport<T>(config: AllPagesExportConfig<T>): Promise<void> {
    const { dataLoader } = config;
    const sorters = config.transformations.sort?.map(v => SortBy.createFromTransformationsSort(v));
    const filters: RequestCriteria = config.transformations.filters ?? { operator: Operator.and };

    let succeeded = false;
    this.lastSavedFileName = null;
    try {
      if (config.allDataPreloaded && dataLoader.getAllLoadedData) {
        // Cały zbiór jest już w pamięci (np. tryb dzienny) - bez fazy pobierania stron ani
        // probe'a totalRows; dane bierzemy prosto z pamięci i od razu konwertujemy.
        const allData = dataLoader.getAllLoadedData(filters, sorters);
        if (!this.aborted) {
          await this.exportPreloadedData(config, allData);
        }
      } else {
        const total = await this.resolveTotal(dataLoader, filters);
        if (this.aborted) return;
        if (total === 0) return;

        const maxRows = config.maxExportRows ?? DEFAULT_MAX_EXPORT_ROWS;
        if (total != null && total > maxRows) {
          this.zone.run(() => this.toast.showWarnToast('local-print.max-rows-exceeded', 'error', { total, max: maxRows }));
          return;
        }

        const resolvedConfig: ResolvedExportConfig<T> = { ...config, total: total ?? 0 };

        if (resolvedConfig.exportPageSize != null && dataLoader.fetchPage) {
          await this.runExportWithPagination(resolvedConfig, filters, sorters);
        } else {
          const obs = dataLoader.fetchWithProgress?.(filters, sorters, this.fetchAllWindow(resolvedConfig));
          if (obs) {
            await this.runExportWithSingleProgress(resolvedConfig, obs);
          } else if (dataLoader.fetchPage) {
            // No streamed endpoint - paginate anyway (default page size) so the response never
            // has to fit in one JSON string; see DEFAULT_EXPORT_PAGE_SIZE.
            await this.runExportWithPagination({ ...resolvedConfig, exportPageSize: DEFAULT_EXPORT_PAGE_SIZE }, filters, sorters);
          } else {
            await this.runExportWithFetchAll(resolvedConfig, filters, sorters);
          }
        }
      }
      succeeded = true;
    } catch (err) {
      if (!this.aborted) {
        this.showExportError(err);
      }
    } finally {
      this.cancelSignal = null;
      this.activeDataLoader = null;
      if (!this.aborted && succeeded) {
        const elapsedMs = performance.now() - this.exportStartMs;
        const duration = this.formatDuration(elapsedMs);
        const fileName = this.lastSavedFileName;
        this.zone.run(() => this.toast.showInfoToast('local-print.export-done', null, { duration, fileName }));
      }
      this.setState(null);
    }
  }

  /**
   * Row window meaning "everything the current filters match": the probed total when known,
   * else the export row cap (or FETCH_ALL_ROWS when the caller disabled the cap with Infinity,
   * which can't be sent in a request).
   */
  private fetchAllWindow<T>(config: ResolvedExportConfig<T>): number {
    const maxRows = config.maxExportRows ?? DEFAULT_MAX_EXPORT_ROWS;
    return config.total > 0 ? config.total : (Number.isFinite(maxRows) ? maxRows : FETCH_ALL_ROWS);
  }

  /**
   * A stale on-screen row count is not trustworthy - it can drift from what the current
   * filters actually match. A one-row fetchPage probe reads the real total (totalRows) before
   * committing to a potentially expensive "export all" fetch. Sorters are deliberately omitted -
   * the total doesn't depend on row order, and skipping ORDER BY lets the database answer the
   * probe cheaper. Returns null when the total can't be determined (no fetchPage support, or
   * the probe itself failed) - callers treat that as "unknown" rather than "zero rows".
   */
  private async resolveTotal(
    dataLoader: DataLoader,
    filters: RequestCriteria
  ): Promise<number | null> {
    if (!dataLoader.fetchPage) return null;
    try {
      const cancel = this.cancelSignal ?? new Subject<void>();
      const page = await dataLoader.fetchPage(0, COUNT_PROBE_ROWS, filters).pipe(takeUntil(cancel)).toPromise();
      return page?.totalRows ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Routes raw rows into the xlsx/csv output as they arrive, whether they came from one
   * streamed response (runExportWithSingleProgress) or many small page requests
   * (runExportWithPagination) - both need identical chunk-to-output handling.
   */
  private createChunkSink<T>(config: AllPagesExportConfig<T>): {
    handleChunk: (rawChunk: any[]) => void;
    streamedCount: () => number;
    finish: () => Promise<void>;
  } {
    const { exportType, dataLoader, columns } = config;
    let xlsxStarted = false;
    const csvParts: string[] = [];
    let csvHeaderWritten = false;
    let streamedCount = 0;

    const handleChunk = (rawChunk: any[]): void => {
      if (this.aborted || rawChunk.length === 0) return;
      dataLoader.applyMutations?.(rawChunk);
      const startIndex = streamedCount;
      const printable = this.printService.toPrintableRows(rawChunk, columns, startIndex);
      streamedCount += rawChunk.length;

      if (exportType === 'xlsx' && printable.length > 0) {
        if (!xlsxStarted) {
          this.printService.initExcelStream(Object.keys(printable[0]));
          xlsxStarted = true;
        }
        this.printService.sendExcelChunk(printable);
      } else if (exportType === 'csv' && printable.length > 0) {
        const csvChunk = this.printService.csvChunkFromPrintable(printable, !csvHeaderWritten);
        csvParts.push(csvHeaderWritten ? csvChunk : '﻿' + csvChunk);
        csvHeaderWritten = true;
      }
    };

    const finish = async (): Promise<void> => {
      if (this.aborted) {
        if (xlsxStarted) this.printService.cancelAllExport();
        return;
      }
      if (exportType === 'xlsx' && xlsxStarted) {
        this.lastSavedFileName = await this.printService.finishExcelStream(undefined, pct =>
          this.setState({ exportType, phase: 'converting', progress: 90 + Math.round(pct * 0.1), fetchedBytes: 0 })
        );
      } else if (exportType === 'csv' && csvParts.length > 0) {
        this.lastSavedFileName = this.printService.saveCsvBlob(csvParts);
      }
    };

    return { handleChunk, streamedCount: () => streamedCount, finish };
  }

  /**
   * Fetches "export all" data as a sequence of small page requests instead of one big
   * request, so a single query never risks the origin's/CDN's per-request timeout
   * (e.g. Cloudflare 524). Page size is configured per table (DataSource.exportPageSize).
   */
  private async runExportWithPagination<T>(
    config: ResolvedExportConfig<T>,
    filters: RequestCriteria,
    sorters: SortBy[]
  ): Promise<void> {
    const { exportType, dataLoader, exportPageSize, total } = config;
    const streaming = exportType !== 'pdf';
    const convertWeight = exportType === 'xlsx' ? 90 : 100;
    const sink = this.createChunkSink(config);
    const pdfRows: any[] = [];
    let fetchedCount = 0;

    for (let start = 0; ; start += exportPageSize) {
      if (this.aborted) break;

      let page: { data: any[]; totalRows: number };
      try {
        const cancel = this.cancelSignal ?? new Subject<void>();
        page = await dataLoader.fetchPage(start, start + exportPageSize, filters, sorters)
          .pipe(takeUntil(cancel))
          .toPromise();
      } catch (err) {
        if (!this.aborted) throw err;
        break;
      }
      if (this.aborted || !page?.data?.length) break;

      fetchedCount += page.data.length;
      if (streaming) {
        sink.handleChunk(page.data);
        const pct = total > 0 ? Math.round(fetchedCount / total * convertWeight) : 0;
        this.setState({ exportType, phase: 'converting', progress: pct, fetchedBytes: 0 });
      } else {
        pdfRows.push(...page.data);
        const pct = total > 0 ? Math.round(fetchedCount / total * 100) : 0;
        this.setState({ exportType, phase: 'fetching', progress: pct, fetchedBytes: 0 });
      }

      if (page.data.length < exportPageSize || (total > 0 && fetchedCount >= total)) break;
    }

    if (this.aborted) {
      if (streaming) await sink.finish();
      return;
    }

    if (!streaming) {
      this.setState({ exportType, phase: 'converting', progress: 0, fetchedBytes: 0 });
      await this.buildAndExportPdf(pdfRows, config);
      return;
    }

    await sink.finish();
  }

  /**
   * xlsx/csv are streamed straight from the parser as chunks arrive - a row never has
   * to sit twice in memory (once raw, once printable) for the whole dataset at once.
   * pdf still needs every row up-front since exportToPdfFromPrintable lays out pages as a batch.
   */
  private async runExportWithSingleProgress<T>(
    config: ResolvedExportConfig<T>,
    obs: Observable<HttpEvent<any>>
  ): Promise<void> {
    const { exportType, total, pageSize } = config;
    const chunkSize = Math.max(pageSize, 2000);
    const streaming = exportType !== 'pdf';
    const convertWeight = exportType === 'xlsx' ? 90 : 100;

    let lastFetchedBytes = 0;
    let responseReceived = false;
    const sink = this.createChunkSink(config);

    const rows = await new Promise<any[]>((resolve, reject) => {
      const cancel = this.cancelSignal ?? new Subject<void>();
      obs.pipe(takeUntil(cancel)).subscribe({
        next: (event: HttpEvent<any>) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const bytePct = event.total ? Math.round(event.loaded / event.total * 100) : 0;
            const progress = streaming ? bytePct : Math.round(bytePct * 0.5);
            lastFetchedBytes = event.loaded;
            this.setState({ exportType, phase: 'fetching', progress, fetchedBytes: event.loaded });
          } else if (event.type === HttpEventType.Response) {
            responseReceived = true;
            const buffer = event.body as ArrayBuffer | null;
            if (!buffer?.byteLength) {
              if (lastFetchedBytes > 10_000) {
                reject(new Error(`Download progress reported ${lastFetchedBytes} B, but the response (event.body) is empty.`));
              } else {
                resolve([]);
              }
              return;
            }

            if (streaming) {
              this.setState({ exportType, phase: 'converting', progress: 0, fetchedBytes: lastFetchedBytes });
            }

            extractJsonArrayAtPath(buffer, ['response', 'data'], {
              chunkSize,
              accumulate: !streaming,
              onChunk: streaming ? sink.handleChunk : undefined,
              shouldAbort: () => this.aborted,
              onProgress: parsedCount => {
                const parsePct = total > 0 ? Math.round(parsedCount / total * 100) : 0;
                if (streaming) {
                  this.setState({ exportType, phase: 'converting', progress: Math.round(parsePct * convertWeight / 100), fetchedBytes: 0 });
                } else {
                  this.setState({ exportType, phase: 'fetching', progress: 50 + Math.round(parsePct * 0.5), fetchedBytes: lastFetchedBytes });
                }
              }
            }).then(parsedRows => {
              const receivedNothing = streaming ? sink.streamedCount() === 0 : parsedRows.length === 0;
              if (receivedNothing && buffer.byteLength > 10_000) {
                const preview = new TextDecoder().decode(buffer.slice(0, 300));
                reject(new Error(`Received ${buffer.byteLength} B of response, but 0 rows after parsing. Start of response: ${preview}`));
                return;
              }
              resolve(parsedRows);
            }, reject);
          }
        },
        error: reject,
        complete: () => { if (!responseReceived) resolve([]); }
      });
    });

    if (this.aborted) {
      if (streaming) await sink.finish();
      return;
    }

    if (!streaming) {
      this.setState({ exportType, phase: 'converting', progress: 0, fetchedBytes: 0 });
      await this.buildAndExportPdf(rows, config);
      return;
    }

    await sink.finish();
  }

  private async buildAndExportPdf<T>(rows: any[], config: AllPagesExportConfig<T>): Promise<void> {
    if (this.aborted) return;
    const { dataLoader, columns, exportType, pageSize } = config;
    const chunkSize = Math.max(pageSize, 2000);
    const total = rows.length;
    const pdfPrintable: Record<string, string>[] = [];

    for (let i = 0; i < rows.length; i += chunkSize) {
      if (this.aborted) return;

      const chunk = rows.slice(i, i + chunkSize);
      dataLoader.applyMutations?.(chunk);
      pdfPrintable.push(...this.printService.toPrintableRows(chunk, columns, i));

      const pct = total > 0 ? Math.round((i + chunk.length) / total * 90) : 0;
      this.setState({ exportType, phase: 'converting', progress: pct, fetchedBytes: 0 });

      await new Promise<void>(r => setTimeout(r, 0));
    }

    if (this.aborted || pdfPrintable.length === 0) return;

    this.lastSavedFileName = await this.printService.exportToPdfFromPrintable(pdfPrintable, undefined, undefined, pct =>
      this.setState({ exportType, phase: 'converting', progress: 90 + Math.round(pct * 0.1), fetchedBytes: 0 })
    );
  }

  private async runExportWithFetchAll<T>(
    config: AllPagesExportConfig<T>,
    filters: RequestCriteria,
    sorters: SortBy[]
  ): Promise<void> {
    const { dataLoader } = config;

    let allData: T[];
    try {
      allData = await dataLoader.fetchAll(filters, sorters).toPromise();
    } catch (err) {
      if (!this.aborted) throw err;
      return;
    }
    if (this.aborted) return;

    await this.exportPreloadedData(config, allData);
  }

  /**
   * Converts an already-materialized row set (fetchAll result, or the daily mode's in-memory
   * getAllLoadedData) straight to the chosen output - no fetching, no per-page progress.
   */
  private async exportPreloadedData<T>(config: AllPagesExportConfig<T>, allData: T[]): Promise<void> {
    const { columns, exportType, pageSize } = config;

    this.setState({ exportType, phase: 'converting', progress: 0, fetchedBytes: 0 });

    if (exportType === 'csv') {
      const printable = this.printService.toPrintableRows(allData, columns, 0);
      if (printable.length > 0) {
        this.lastSavedFileName = this.printService.saveCsvBlob(['﻿' + this.printService.csvChunkFromPrintable(printable, true)]);
      }

    } else if (exportType === 'xlsx') {
      let xlsxStarted = false;
      for (let i = 0; i < allData.length; i += pageSize) {
        if (this.aborted) break;
        const chunk = this.printService.toPrintableRows(allData.slice(i, i + pageSize), columns, i);
        if (chunk.length > 0) {
          if (!xlsxStarted) {
            this.printService.initExcelStream(Object.keys(chunk[0]));
            xlsxStarted = true;
          }
          this.printService.sendExcelChunk(chunk);
        }
      }
      if (xlsxStarted) {
        if (!this.aborted) {
          this.lastSavedFileName = await this.printService.finishExcelStream(undefined, pct =>
            this.setState({ exportType, phase: 'converting', progress: pct, fetchedBytes: 0 })
          );
        } else {
          this.printService.cancelAllExport();
        }
      }

    } else if (exportType === 'pdf') {
      const printable = this.printService.toPrintableRows(allData, columns, 0);
      if (printable.length > 0) {
        this.lastSavedFileName = await this.printService.exportToPdfFromPrintable(printable, undefined, undefined, pct =>
          this.setState({ exportType, phase: 'converting', progress: pct, fetchedBytes: 0 })
        );
      }
    }
  }

  private formatDuration(ms: number): string {
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 60) return `${totalSec}s`;
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
  }
}
